import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  mockJudicialRequirements,
  mockJudicialDashboard,
  getSemaforoColor,
} from "@/mocks/judicialRequirements";
import type { JudicialRequirement, JudicialRequirementState } from "@/mocks/judicialRequirements";
import JudicialRequirementCard from "./components/JudicialRequirementCard";
import ScrollReveal, { ScrollRevealContainer } from "@/components/feature/ScrollReveal";
import { judicialService } from "@/services/api";
import { mapDbJudicialToFrontend } from "@/services/mapping";

type FilterState = "todos" | "rojo" | "amarillo" | "verde" | "vencido" | "activo";

export default function JudicialPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterState>("activo");
  const [search, setSearch] = useState("");
  const [requirements, setRequirements] = useState<JudicialRequirement[]>([]);
  const [loading, setLoading] = useState(true);
  const [usingMock, setUsingMock] = useState(false);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const data = await judicialService.list();
        if (Array.isArray(data) && data.length > 0) {
          setRequirements(data.map(mapDbJudicialToFrontend));
          setUsingMock(false);
        } else {
          setRequirements(mockJudicialRequirements);
          setUsingMock(true);
        }
      } catch (err) {
        console.warn("Error loading judicial requirements, using mock fallback:", err);
        setRequirements(mockJudicialRequirements);
        setUsingMock(true);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const filtered = useMemo(() => {
    let reqs = [...requirements];

    // Filter by status
    switch (filter) {
      case "rojo": reqs = reqs.filter((r) => r.semaforo === "rojo"); break;
      case "amarillo": reqs = reqs.filter((r) => r.semaforo === "amarillo"); break;
      case "verde": reqs = reqs.filter((r) => r.semaforo === "verde"); break;
      case "vencido": reqs = reqs.filter((r) => r.estado === "vencido"); break;
      case "activo": reqs = reqs.filter((r) => r.estado !== "cerrado" && r.estado !== "enviado_tribunal"); break;
    }

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      reqs = reqs.filter(
        (r) =>
          r.tribunal.toLowerCase().includes(q) ||
          r.rit.toLowerCase().includes(q) ||
          (r.studentName && r.studentName.toLowerCase().includes(q)) ||
          r.tipoRequerimiento.toLowerCase().includes(q)
      );
    }

    return reqs;
  }, [requirements, filter, search]);

  const rojos = useMemo(() => requirements.filter((r) => r.semaforo === "rojo").length, [requirements]);
  const amarillos = useMemo(() => requirements.filter((r) => r.semaforo === "amarillo").length, [requirements]);
  const verdes = useMemo(() => requirements.filter((r) => r.semaforo === "verde").length, [requirements]);
  const activos = useMemo(() => requirements.filter((r) => r.estado !== "cerrado").length, [requirements]);
  const enviados = useMemo(() => requirements.filter((r) => r.estado === "enviado_tribunal").length, [requirements]);
  const vencidos = useMemo(() => requirements.filter((r) => r.estado === "vencido").length, [requirements]);

  const dashboard = useMemo(() => {
    if (usingMock) return mockJudicialDashboard;

    const tribunalCounts: Record<string, number> = {};
    requirements.forEach((r) => {
      if (r.tribunal) {
        tribunalCounts[r.tribunal] = (tribunalCounts[r.tribunal] || 0) + 1;
      }
    });

    const requerimientosPorTribunal = Object.entries(tribunalCounts)
      .map(([tribunal, cantidad]) => ({ tribunal, cantidad }))
      .sort((a, b) => b.cantidad - a.cantidad);

    return {
      ...mockJudicialDashboard,
      totalActivos: activos,
      proximosVencer: amarillos,
      vencidos,
      informesPendientes: activos - enviados,
      informesEnviados: enviados,
      requerimientosPorTribunal: requerimientosPorTribunal.length > 0 ? requerimientosPorTribunal : mockJudicialDashboard.requerimientosPorTribunal,
    };
  }, [requirements, usingMock, activos, amarillos, vencidos, enviados]);

  return (
    <div className="space-y-6">
      {usingMock && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs px-4 py-2.5 rounded-xl flex items-center gap-2 animate-fade-in">
          <i className="ri-information-line text-sm flex-shrink-0" />
          <span>Modo Demo: Mostrando requerimientos judiciales simulados del sistema local.</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <span className="flex items-center justify-center w-8 h-8">
            <i className="ri-loader-4-line text-2xl text-foreground-300 animate-spin" />
          </span>
        </div>
      ) : (
        <>
          {/* Header */}
          <ScrollReveal className="scroll-reveal">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-foreground-900 tracking-tight">
                  Requerimientos Judiciales
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  Gestión de oficios, informes y requerimientos de tribunales
                </p>
              </div>
              <button
                onClick={() => navigate("/judicial/nuevo")}
                className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-white rounded-xl text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-soft self-start"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-add-line" />
                </span>
                Nuevo Requerimiento
              </button>
            </div>
          </ScrollReveal>

          {/* KPI Cards */}
          <ScrollRevealContainer staggerDelay={60}>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <KpiCard label="Activos" value={activos} icon="ri-folder-3-line" color="bg-primary-50 text-primary-600" />
              <KpiCard label="En rojo" value={rojos} icon="ri-error-warning-line" color="bg-red-50 text-red-600" pulse />
              <KpiCard label="En amarillo" value={amarillos} icon="ri-alert-line" color="bg-amber-50 text-amber-600" />
              <KpiCard label="Al día" value={verdes} icon="ri-check-double-line" color="bg-emerald-50 text-emerald-600" />
              <KpiCard label="Enviados" value={dashboard.informesEnviados} icon="ri-send-plane-line" color="bg-sky-50 text-sky-600" />
              <KpiCard label="Vencidos" value={dashboard.vencidos} icon="ri-close-circle-line" color="bg-red-50 text-red-600" urgent />
            </div>
          </ScrollRevealContainer>

          {/* Alertas Críticas */}
          {dashboard.alertasCriticas.filter((a) => a.nivel === "rojo").length > 0 && (
            <ScrollReveal className="scroll-reveal">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-5 h-5 flex items-center justify-center text-red-500">
                    <i className="ri-error-warning-fill" />
                  </span>
                  <span className="text-sm font-bold text-red-700">Alertas Críticas</span>
                  <span className="text-[11px] text-red-500 bg-red-100 px-2 py-0.5 rounded-full font-medium">
                    {dashboard.alertasCriticas.filter((a) => a.nivel === "rojo").length}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {dashboard.alertasCriticas.filter((a) => a.nivel === "rojo").map((alerta, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-red-700 bg-white/60 rounded-lg px-3 py-2">
                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-error-warning-line text-red-500" />
                      </span>
                      <div>
                        <span className="font-medium">{alerta.tipo}</span>
                        {" — "}
                        <span>{alerta.descripcion}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          )}

          {/* Filters + Search */}
          <ScrollReveal className="scroll-reveal">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="flex items-center gap-1.5 bg-background-50 border border-background-200/70 rounded-full p-1">
                {([
                  { key: "activo", label: "Activos" },
                  { key: "rojo", label: "En rojo" },
                  { key: "amarillo", label: "En amarillo" },
                  { key: "verde", label: "Al día" },
                  { key: "vencido", label: "Vencidos" },
                  { key: "todos", label: "Todos" },
                ] as { key: FilterState; label: string }[]).map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      filter === f.key
                        ? "bg-white text-foreground-900 shadow-sm"
                        : "text-foreground-500 hover:text-foreground-700"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              <div className="relative flex-1 max-w-xs ml-auto">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400">
                  <i className="ri-search-line text-sm" />
                </span>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por tribunal, RIT o estudiante..."
                  className="w-full pl-9 pr-3 py-2 bg-background-50 border border-background-200/70 rounded-xl text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                />
              </div>
            </div>
          </ScrollReveal>

          {/* Requeriments Grid */}
          <ScrollRevealContainer staggerDelay={80}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {filtered.map((req, idx) => (
                <div key={req.id} className="scroll-reveal" style={{ transitionDelay: `${idx * 80}ms` }}>
                  <JudicialRequirementCard requirement={req} />
                </div>
              ))}
            </div>
          </ScrollRevealContainer>

          {filtered.length === 0 && (
            <div className="text-center py-12 scroll-reveal">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
                <i className="ri-scales-3-line text-2xl text-foreground-300" />
              </div>
              <p className="text-sm text-foreground-500">No se encontraron requerimientos con estos filtros</p>
            </div>
          )}

          {/* Quick Stats Row */}
          <ScrollRevealContainer staggerDelay={100}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Por Tribunal */}
              <div className="bg-white border border-background-200/70 rounded-xl p-4 scroll-reveal">
                <h4 className="text-[11px] font-bold text-foreground-400 uppercase tracking-wider mb-3">
                  Requerimientos por Tribunal
                </h4>
                <div className="space-y-2">
                  {dashboard.requerimientosPorTribunal.slice(0, 5).map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-foreground-700 truncate mr-2">{item.tribunal}</span>
                      <span className="text-foreground-500 font-medium bg-background-50 px-2 py-0.5 rounded-full">{item.cantidad}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Por Curso */}
              <div className="bg-white border border-background-200/70 rounded-xl p-4 scroll-reveal" style={{ transitionDelay: "100ms" }}>
                <h4 className="text-[11px] font-bold text-foreground-400 uppercase tracking-wider mb-3">
                  Casos por Curso
                </h4>
                <div className="space-y-2">
                  {dashboard.casosJudicializadosPorCurso.slice(0, 5).map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-foreground-700 truncate mr-2">{item.curso}</span>
                      <span className="text-foreground-500 font-medium bg-background-50 px-2 py-0.5 rounded-full">{item.cantidad}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Cumplimiento Mensual */}
              <div className="bg-white border border-background-200/70 rounded-xl p-4 scroll-reveal" style={{ transitionDelay: "200ms" }}>
                <h4 className="text-[11px] font-bold text-foreground-400 uppercase tracking-wider mb-3">
                  Cumplimiento Mensual 2026
                </h4>
                <div className="space-y-2">
                  {dashboard.cumplimientoMensual.map((item, i) => {
                    const pct = item.recibidos > 0 ? Math.round((item.respondidos / item.recibidos) * 100) : 0;
                    return (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className="w-10 text-foreground-500 font-medium">{item.mes}</span>
                        <div className="flex-1 h-2 bg-background-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${
                              pct === 100 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-red-500"
                            }`}
                            style={{ width: `${Math.max(pct, 5)}%` }}
                          />
                        </div>
                        <span className="w-14 text-right text-foreground-500 tabular-nums">
                          {item.respondidos}/{item.recibidos}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 pt-3 border-t border-background-200/50 flex items-center justify-between">
                  <span className="text-[11px] text-foreground-500">Tiempo promedio de respuesta</span>
                  <span className="text-sm font-bold text-foreground-900">{dashboard.tiempoPromedioRespuesta}</span>
                </div>
              </div>
            </div>
          </ScrollRevealContainer>
        </>
      )}
    </div>
  );
}

function KpiCard({
  label,
  value,
  icon,
  color,
  pulse,
  urgent,
}: {
  label: string;
  value: number;
  icon: string;
  color: string;
  pulse?: boolean;
  urgent?: boolean;
}) {
  return (
    <div className={`bg-white border ${urgent ? "border-red-200" : "border-background-200/70"} rounded-xl p-4 scroll-reveal`}>
      <div className="flex items-center gap-2.5">
        <div className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center flex-shrink-0`}>
          <i className={`${icon} ${pulse ? "animate-pulse" : ""} text-sm`} />
        </div>
        <div>
          <p className="text-2xl font-bold text-foreground-900 tabular-nums">{value}</p>
          <p className="text-[11px] text-foreground-500 font-medium">{label}</p>
        </div>
      </div>
    </div>
  );
}